import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { usesControlledExecution } from '../operate/controlledRunHelpers';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_COLORS = { Open:'#60a5fa', 'In Progress':'#f59e0b', Completed:'#46BB89', Cancelled:'#94a3b8' };
const DEVICE_COLORS = { Active:'#60a5fa', Passed:'#46BB89', Rework:'#f59e0b', Scrapped:'#EC2124' };

function submissionFailure(data, label) {
  if (data?.error === 'event_id_conflict') return 'This submission was already saved with different values. Close this form and review the saved result before creating another submission.';
  if (['history_limit_reached', 'capture_conflict_or_history_limit', 'procedure_snapshot_limit', 'event_size_limit'].includes(data?.error)) return 'This submission needs application support before it can be saved. Your entries are retained; contact the application owner to review the history or procedure size.';
  if (data?.error === 'procedure_revision_unavailable') return 'The requested procedure revision is unavailable. Your entries are retained; review the procedure before submitting again.';
  if (data?.error === 'forbidden') return 'Your account cannot save this submission. Your entries are retained; contact your administrator.';
  return `${label} could not be confirmed. Your entries are retained; retry to confirm the same submission.`;
}

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function StatusBadge({ status, colors = STATUS_COLORS }) {
  const c = colors[status] || '#666';
  return <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11,
    fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', background:c+'22', color:c }}>{status}</span>;
}

export default function WorkOrderDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const hasEditRole = ['admin','quality-manager','production-engineer','service','operator'].includes(role);

  const [order, setOrder] = useState(null);
  const [flow, setFlow]   = useState(null);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeStation, setActiveStation] = useState(null); // stationId
  const [signOffModal, setSignOffModal]   = useState(null); // { serialNumber, station }
  const [testModal, setTestModal]         = useState(null); // { serialNumber, station, test }
  const [scrapModal, setScrapModal]       = useState(null); // { serialNumber }
  const [saving, setSaving]               = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const controlled = Boolean(flow && usesControlledExecution(flow));
  const canEdit = hasEditRole && !controlled;

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/work-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) { setOrder(data.order); setFlow(data.flow); }
    } catch {} finally { setLoading(false); }
  }, [id, getToken]);

  useEffect(() => { load(); }, [load]);

  // Sign off a device at a station
  const signOff = async (serialNumber, station, outcome, reasonCode, notes, clientEventId) => {
    if (!canEdit) return;
    setSaving(true);
    setSubmissionError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/work-orders/${id}/sign-off`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ serialNumber, stationId: station.stationId, stationName: station.name, outcome, reasonCode, notes, clientEventId }),
      });
      const data = await res.json();
      if (data.ok) { setOrder(data.order); setSignOffModal(null); }
      else setSubmissionError(submissionFailure(data, 'Sign-off'));
    } catch { setSubmissionError('Sign-off could not be confirmed. Your entries are retained; retry to confirm the same submission.'); } finally { setSaving(false); }
  };

  // Mark device final status (Passed, Scrapped, back to Active)
  const markDevice = async (serialNumber, status, scrappedReason, scrappedNotes) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/work-orders/${id}/device-status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ serialNumber, status, scrappedReason, scrappedNotes }),
      });
      const data = await res.json();
      if (data.ok) { setOrder(data.order); setScrapModal(null); }
    } catch {} finally { setSaving(false); }
  };

  // Submit test session
  const submitTest = async (serialNumber, station, test, steps, clientEventId) => {
    if (!canEdit) return;
    setSaving(true);
    setSubmissionError('');
    const allPassed = steps.every(s => s.outcome === 'pass' || s.outcome === 'skip');
    const anyFailed = steps.some(s => s.outcome === 'fail');
    const outcome = anyFailed ? 'fail' : allPassed ? 'pass' : 'pending';
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/work-orders/${id}/test-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serialNumber, stationId: station.stationId,
          testRefId: test.refId, testDocNumber: test.documentNumber,
          testName: test.name, steps, outcome, clientEventId,
        }),
      });
      const data = await res.json();
      if (data.ok) { setOrder(data.order); setTestModal(null); }
      else setSubmissionError(submissionFailure(data, 'Test submission'));
    } catch { setSubmissionError('Test submission could not be confirmed. Your entries are retained; retry to confirm the same submission.'); } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading…</div>;
  if (!order)  return <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Work order not found.</div>;

  const stations = flow?.stations || [];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:20 }}>
        <button onClick={() => navigate('/quality/execution')}
          style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18, paddingTop:2 }}>←</button>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <h2 style={{ margin:0, fontSize:20, fontWeight:800, fontFamily:'monospace' }}>{order.workOrderNumber}</h2>
            <StatusBadge status={order.status} />
          </div>
          <div style={{ fontSize:13, color:'var(--ni-text-muted)', marginTop:4 }}>
            <span style={{ fontFamily:'monospace', color:'#60a5fa' }}>{order.flowDocNumber}</span>
            {' '}{order.flowName} · Rev {order.flowRevision}
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {order.status === 'Open' && canEdit && (
            <button onClick={async () => {
              const token = await getToken();
              await window.fetch(`${API_BASE}/quality/work-orders/${id}`, {
                method:'PATCH', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
                body: JSON.stringify({ status: 'In Progress' }),
              });
              load();
            }} style={{ background:'#f59e0b22', border:'1px solid #f59e0b44', color:'#f59e0b',
              borderRadius:6, padding:'6px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Start
            </button>
          )}
          {order.status === 'In Progress' && canEdit && (
            <button onClick={async () => {
              const token = await getToken();
              await window.fetch(`${API_BASE}/quality/work-orders/${id}`, {
                method:'PATCH', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
                body: JSON.stringify({ status: 'Completed' }),
              });
              load();
            }} style={{ background:'#46BB8922', border:'1px solid #46BB8944', color:'#46BB89',
              borderRadius:6, padding:'6px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              Complete
            </button>
          )}
        </div>
      </div>

      {controlled && <div role="status" style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, padding:16, marginBottom:20 }}>
        <strong>This flow uses controlled station execution.</strong>
        <p style={{ margin:'6px 0 10px', color:'var(--ni-text-muted)' }}>Record timers, results and independent sign-offs in the station workflow. Legacy work-order actions are unavailable.</p>
        <Link to={`/operate/${encodeURIComponent(flow._id || order.flowId)}`} style={{ color:'var(--ni-green, #46BB89)', fontWeight:700 }}>Use station execution</Link>
      </div>}

      {/* Device summary chips */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        {order.devices.map(d => (
          <span key={d.serialNumber} style={{ fontSize:11, fontFamily:'monospace',
            color: DEVICE_COLORS[d.status] || '#666',
            background: (DEVICE_COLORS[d.status] || '#666') + '18',
            border: `1px solid ${(DEVICE_COLORS[d.status] || '#666')}33`,
            borderRadius:6, padding:'3px 10px' }}>
            {d.serialNumber} · {d.status}
          </span>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:16, alignItems:'start' }}>

        {/* Station list */}
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
            letterSpacing:'0.06em', marginBottom:4 }}>Stations ({stations.length})</div>
          {stations.map(st => {
            const completions = order.devices.map(d =>
              d.stationCompletions.find(c => c.stationId === st.stationId)
            );
            const doneCount = completions.filter(Boolean).length;
            const total = order.devices.length;
            const allDone = doneCount === total;
            const isActive = activeStation === st.stationId;

            return (
              <div key={st.stationId}
                onClick={() => setActiveStation(isActive ? null : st.stationId)}
                style={{ background: isActive ? '#60a5fa18' : 'var(--ni-panel)',
                  border: `1px solid ${isActive ? '#60a5fa55' : 'var(--ni-border-soft)'}`,
                  borderRadius:8, padding:'10px 12px', cursor:'pointer' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                    background: allDone ? '#46BB89' : doneCount > 0 ? '#f59e0b' : '#94a3b8' }} />
                  <span style={{ fontSize:13, fontWeight:600, flex:1 }}>{st.name || st.stationId}</span>
                  <span style={{ fontSize:11, color: allDone ? '#46BB89' : 'var(--ni-text-muted)',
                    fontWeight: allDone ? 700 : 400 }}>{doneCount}/{total}</span>
                </div>
                <div style={{ fontSize:11, color:'var(--ni-text-muted)', marginTop:3, marginLeft:16 }}>{st.type}</div>
                {(st.testProcedures||[]).length > 0 && (
                  <div style={{ fontSize:10, color:'#34d399', marginTop:2, marginLeft:16 }}>
                    {st.testProcedures.length} test{st.testProcedures.length!==1?'s':''}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Station detail panel */}
        <div>
          {!activeStation ? (
            <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
              borderRadius:10, padding:32, textAlign:'center', color:'var(--ni-text-muted)', fontSize:13 }}>
              Select a station to view device progress and sign off
            </div>
          ) : (() => {
            const st = stations.find(s => s.stationId === activeStation);
            if (!st) return null;

            return (
              <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, padding:16 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                  <span style={{ fontSize:16, fontWeight:800 }}>{st.name || st.stationId}</span>
                  <span style={{ fontSize:11, color:'var(--ni-text-muted)', background:'rgba(255,255,255,0.07)',
                    borderRadius:4, padding:'2px 8px' }}>{st.type}</span>
                  {st.estimatedCycleTimeSeconds && (
                    <span style={{ fontSize:11, color:'var(--ni-text-muted)' }}>
                      ~{Math.round(st.estimatedCycleTimeSeconds/60)} min
                    </span>
                  )}
                </div>

                {/* Resources */}
                {((st.jigs||[]).length+(st.equipment||[]).length+(st.testProcedures||[]).length) > 0 && (
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
                    {(st.jigs||[]).map(j => (
                      <span key={j.refId} style={{ fontSize:11, color:'#a78bfa', background:'#a78bfa18',
                        border:'1px solid #a78bfa33', borderRadius:4, padding:'2px 8px', fontFamily:'monospace' }}>
                        🔩 {j.documentNumber||j.name}
                      </span>
                    ))}
                    {(st.equipment||[]).map(e => (
                      <span key={e.refId} style={{ fontSize:11, color:'#f59e0b', background:'#f59e0b18',
                        border:'1px solid #f59e0b33', borderRadius:4, padding:'2px 8px', fontFamily:'monospace' }}>
                        ⚙ {e.documentNumber||e.name}
                      </span>
                    ))}
                    {(st.testProcedures||[]).map(t => (
                      <span key={t.refId} style={{ fontSize:11, color:'#34d399', background:'#34d39918',
                        border:'1px solid #34d39933', borderRadius:4, padding:'2px 8px', fontFamily:'monospace' }}>
                        🧪 {t.documentNumber||t.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Device rows */}
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {order.devices.map(d => {
                    const completion = d.stationCompletions.find(c => c.stationId === st.stationId);
                    const lastTest   = [...d.testSessions].reverse().find(s => s.stationId === st.stationId);
                    const oc = completion?.outcome;
                    const outcomeColor = { pass:'#46BB89', fail:'#EC2124', rework:'#f59e0b', skip:'#94a3b8' }[oc] || null;

                    return (
                      <div key={d.serialNumber} style={{ border:'1px solid var(--ni-border-soft)',
                        borderRadius:8, padding:'10px 14px',
                        background: completion ? (outcomeColor||'#46BB89')+'10' : 'rgba(255,255,255,0.03)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, flex:1 }}>{d.serialNumber}</span>
                          <StatusBadge status={d.status} colors={DEVICE_COLORS} />
                          {completion && (
                            <span style={{ fontSize:11, fontWeight:700,
                              color: outcomeColor, background: outcomeColor+'22',
                              borderRadius:4, padding:'2px 8px', textTransform:'uppercase' }}>
                              {oc}
                            </span>
                          )}
                        </div>

                        {completion && (
                          <div style={{ fontSize:11, color:'var(--ni-text-muted)', marginTop:4 }}>
                            Signed off by {completion.signedOffByName} · {new Date(completion.signedOffAt).toLocaleString()}
                            {completion.notes && <span> · {completion.notes}</span>}
                          </div>
                        )}

                        {lastTest && (
                          <div style={{ fontSize:11, marginTop:4 }}>
                            <span style={{ color:'#34d399' }}>Last test: {lastTest.testName}</span>
                            {' '}<span style={{ color: lastTest.outcome==='pass'?'#46BB89':lastTest.outcome==='fail'?'#EC2124':'#f59e0b',
                              fontWeight:700, textTransform:'uppercase' }}>{lastTest.outcome}</span>
                          </div>
                        )}

                        {canEdit && (
                          <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                            {d.status !== 'Scrapped' && (
                              <button onClick={() => { setSubmissionError(''); setSignOffModal({ serialNumber: d.serialNumber, station: st, clientEventId: window.crypto.randomUUID() }); }}
                                style={{ background:'#46BB8922', border:'1px solid #46BB8944', color:'#46BB89',
                                  borderRadius:5, padding:'4px 12px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                                {completion ? 'Re-sign Off' : 'Sign Off'}
                              </button>
                            )}
                            {(st.testProcedures||[]).map(t => (
                              <button key={t.refId}
                                onClick={() => { setSubmissionError(''); setTestModal({ serialNumber: d.serialNumber, station: st, test: t, clientEventId: window.crypto.randomUUID() }); }}
                                style={{ background:'#34d39922', border:'1px solid #34d39944', color:'#34d399',
                                  borderRadius:5, padding:'4px 12px', fontSize:12, cursor:'pointer' }}>
                                Run: {t.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {canEdit && (d.status === 'Active' || d.status === 'Rework') && (
                          <div style={{ display:'flex', gap:6, marginTop:6, paddingTop:6,
                            borderTop:'1px solid rgba(255,255,255,0.07)', flexWrap:'wrap' }}>
                            <button onClick={() => markDevice(d.serialNumber, 'Passed')} disabled={saving}
                              style={{ background:'#46BB8915', border:'1px solid #46BB8933', color:'#46BB89',
                                borderRadius:5, padding:'3px 10px', fontSize:11, cursor:'pointer', fontWeight:700 }}>
                              ✓ Mark Passed
                            </button>
                            <button onClick={() => setScrapModal({ serialNumber: d.serialNumber })}
                              style={{ background:'#EC212415', border:'1px solid #EC212433', color:'#EC2124',
                                borderRadius:5, padding:'3px 10px', fontSize:11, cursor:'pointer', fontWeight:700 }}>
                              Scrap
                            </button>
                          </div>
                        )}
                        {canEdit && d.status === 'Rework' && (
                          <div style={{ marginTop:4 }}>
                            <button onClick={() => markDevice(d.serialNumber, 'Active')} disabled={saving}
                              style={{ background:'none', border:'none', color:'var(--ni-text-muted)',
                                fontSize:11, cursor:'pointer', padding:0, textDecoration:'underline' }}>
                              Re-activate
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Sign-off modal */}
      {signOffModal && canEdit && (
        <SignOffModal
          serialNumber={signOffModal.serialNumber}
          station={signOffModal.station}
          onClose={() => { setSignOffModal(null); load(); }}
          onSubmit={(outcome, reasonCode, notes) => signOff(signOffModal.serialNumber, signOffModal.station, outcome, reasonCode, notes, signOffModal.clientEventId)}
          error={submissionError}
          saving={saving}
        />
      )}

      {/* Test execution modal */}
      {testModal && canEdit && (
        <TestExecutionModal
          serialNumber={testModal.serialNumber}
          station={testModal.station}
          test={testModal.test}
          getToken={getToken}
          onClose={() => { setTestModal(null); load(); }}
          onSubmit={(steps) => submitTest(testModal.serialNumber, testModal.station, testModal.test, steps, testModal.clientEventId)}
          error={submissionError}
          saving={saving}
        />
      )}

      {/* Scrap modal */}
      {scrapModal && canEdit && (
        <ScrapModal
          serialNumber={scrapModal.serialNumber}
          onClose={() => setScrapModal(null)}
          onSubmit={(reason, notes) => markDevice(scrapModal.serialNumber, 'Scrapped', reason, notes)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Sign-off modal ────────────────────────────────────────────────────────────

const REWORK_REASON_CODES = [
  'Component defect', 'Assembly error', 'Solder defect', 'Firmware issue',
  'Calibration failure', 'Cosmetic damage', 'Test failure', 'Other',
];

function SignOffModal({ serialNumber, station, onClose, onSubmit, saving, error }) {
  const [outcome, setOutcome]   = useState('pass');
  const [reasonCode, setReason] = useState('');
  const [notes, setNotes]       = useState('');
  const needsReason = outcome === 'rework' || outcome === 'fail';
  const inp = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'8px 12px', fontSize:13, width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12,
        padding:24, width:420, maxWidth:'90%' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>Sign Off Station</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        <div style={{ fontSize:13, color:'var(--ni-text-muted)', marginBottom:16 }}>
          <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--ni-text)' }}>{serialNumber}</span>
          {' '}at{' '}
          <span style={{ fontWeight:700, color:'var(--ni-text)' }}>{station.name}</span>
        </div>
        {error && <div role="alert" style={{ color:'var(--ni-danger, #EC2124)', marginBottom:12 }}>{error}</div>}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.06em', marginBottom:6 }}>Outcome</div>
            <div style={{ display:'flex', gap:8 }}>
              {[['pass','#46BB89'],['fail','#EC2124'],['rework','#f59e0b'],['skip','#94a3b8']].map(([v,c]) => (
                <button key={v} onClick={() => { setOutcome(v); if (v === 'pass' || v === 'skip') setReason(''); }}
                  style={{ flex:1, padding:'8px', borderRadius:6, fontWeight:700, fontSize:12, cursor:'pointer',
                    textTransform:'uppercase', border: `1px solid ${outcome===v?c:c+'44'}`,
                    background: outcome===v ? c+'33' : 'transparent',
                    color: outcome===v ? c : c+'88' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          {needsReason && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
                letterSpacing:'0.06em', marginBottom:5 }}>Reason Code</div>
              <select value={reasonCode} onChange={e => setReason(e.target.value)} style={inp}>
                <option value="">— Select reason —</option>
                {REWORK_REASON_CODES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.06em', marginBottom:5 }}>Notes (optional)</div>
            <input value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="Any observations…" />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => onSubmit(outcome, reasonCode, notes)} disabled={saving}
              style={{ background:'#46BB89', color:'#000', border:'none', borderRadius:6,
                padding:'9px 20px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              {saving ? 'Saving…' : 'Confirm Sign Off'}
            </button>
            <button onClick={onClose}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'9px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Test execution modal ──────────────────────────────────────────────────────

function TestExecutionModal({ serialNumber, station, test, getToken, onClose, onSubmit, saving, error }) {
  const [steps, setSteps]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!test.refId) { setLoading(false); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/test-library/${test.refId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => {
        if (d.ok) {
          setSteps((d.entry.steps || []).map((s, i) => ({
            stepIndex: i, instruction: s.instruction, expectedResult: s.expectedResult,
            evidenceType: s.evidenceType, minValue: s.minValue, maxValue: s.maxValue, unit: s.unit,
            warningNote: s.warningNote, acceptanceCriteria: s.acceptanceCriteria,
            outcome: 'pending', measuredValue: '', notes: '',
          })));
        }
        setLoading(false);
      })
    );
  }, [test.refId, getToken]);

  const setStep = (i, key, val) => setSteps(ss => ss.map((s, j) => j === i ? { ...s, [key]: val } : s));

  const passFail = (i, outcome) => setStep(i, 'outcome', outcome);

  const passAll = () => setSteps(ss => ss.map(s => ({ ...s, outcome: 'pass' })));

  const allDone = steps.length > 0 && steps.every(s => s.outcome !== 'pending');
  const anyFail = steps.some(s => s.outcome === 'fail');

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--ni-char)', border:'1px solid var(--ni-border-soft)', borderRadius:12,
        padding:24, width:640, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
          <span style={{ fontWeight:700, fontSize:16 }}>{test.name}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginBottom:16 }}>
          <span style={{ fontFamily:'monospace' }}>{test.documentNumber}</span>
          {' '}· Device: <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--ni-text)' }}>{serialNumber}</span>
          {' '}· Station: {station.name}
        </div>

        {error && <div role="alert" style={{ color:'var(--ni-danger, #EC2124)', marginBottom:12 }}>{error}</div>}
        {loading ? (
          <div style={{ padding:32, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading test steps…</div>
        ) : steps.length === 0 ? (
          <div style={{ padding:32, textAlign:'center', color:'var(--ni-text-muted)' }}>
            This test has no steps defined. Sign off the station directly.
          </div>
        ) : (
          <>
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
              <button onClick={passAll}
                style={{ background:'#46BB8922', border:'1px solid #46BB8944', color:'#46BB89',
                  borderRadius:6, padding:'5px 14px', fontSize:12, cursor:'pointer' }}>
                Pass All Steps
              </button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
              {steps.map((s, i) => (
                <div key={i} style={{ border:`1px solid ${s.outcome==='pass'?'#46BB8944':s.outcome==='fail'?'#EC212444':'var(--ni-border-soft)'}`,
                  borderRadius:8, padding:'12px 14px',
                  background: s.outcome==='pass'?'#46BB8910':s.outcome==='fail'?'#EC212410':'rgba(255,255,255,0.02)' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:6 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:'var(--ni-text-muted)',
                      minWidth:24, flexShrink:0 }}>{i+1}.</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600 }}>{s.instruction}</div>
                      {s.expectedResult && (
                        <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:2 }}>
                          Expected: {s.expectedResult}
                        </div>
                      )}
                      {s.acceptanceCriteria && (
                        <div style={{ fontSize:12, color:'#60a5fa', marginTop:2 }}>
                          Criteria: {s.acceptanceCriteria}
                        </div>
                      )}
                      {s.warningNote && (
                        <div style={{ fontSize:12, color:'#f59e0b', marginTop:2 }}>
                          ⚠ {s.warningNote}
                        </div>
                      )}
                      {(s.minValue != null || s.maxValue != null) && (
                        <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:2 }}>
                          Range: {s.minValue ?? '—'} – {s.maxValue ?? '—'}{s.unit ? ` ${s.unit}` : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display:'flex', gap:8, alignItems:'center', marginLeft:34, flexWrap:'wrap' }}>
                    {(s.evidenceType === 'Measurement' || (s.minValue != null || s.maxValue != null)) && (
                      <input
                        placeholder={`Value${s.unit ? ` (${s.unit})` : ''}`}
                        value={s.measuredValue}
                        onChange={e => setStep(i, 'measuredValue', e.target.value)}
                        style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
                          color:'var(--ni-text)', borderRadius:6, padding:'5px 10px', fontSize:12, width:120 }}
                      />
                    )}
                    <input
                      placeholder="Notes…"
                      value={s.notes}
                      onChange={e => setStep(i, 'notes', e.target.value)}
                      style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
                        color:'var(--ni-text)', borderRadius:6, padding:'5px 10px', fontSize:12, flex:1, minWidth:100 }}
                    />
                    <button onClick={() => passFail(i, 'pass')}
                      style={{ background:s.outcome==='pass'?'#46BB89':'transparent',
                        border:'1px solid #46BB8966', color:s.outcome==='pass'?'#000':'#46BB89',
                        borderRadius:6, padding:'5px 14px', fontSize:12, cursor:'pointer', fontWeight:700 }}>
                      Pass
                    </button>
                    <button onClick={() => passFail(i, 'fail')}
                      style={{ background:s.outcome==='fail'?'#EC2124':'transparent',
                        border:'1px solid #EC212466', color:s.outcome==='fail'?'#fff':'#EC2124',
                        borderRadius:6, padding:'5px 14px', fontSize:12, cursor:'pointer', fontWeight:700 }}>
                      Fail
                    </button>
                    <button onClick={() => passFail(i, 'skip')}
                      style={{ background:'transparent', border:'1px solid var(--ni-border-soft)',
                        color:'var(--ni-text-muted)', borderRadius:6, padding:'5px 10px', fontSize:12, cursor:'pointer' }}>
                      Skip
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary + submit */}
            <div style={{ borderTop:'1px solid var(--ni-border-soft)', paddingTop:14, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:13 }}>
                {allDone
                  ? anyFail
                    ? <span style={{ color:'#EC2124', fontWeight:700 }}>⚠ {steps.filter(s=>s.outcome==='fail').length} step(s) failed</span>
                    : <span style={{ color:'#46BB89', fontWeight:700 }}>✓ All steps completed</span>
                  : <span style={{ color:'var(--ni-text-muted)' }}>{steps.filter(s=>s.outcome!=='pending').length}/{steps.length} steps done</span>
                }
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => onSubmit(steps)} disabled={saving || !allDone}
                  style={{ background: anyFail?'#EC2124':'#46BB89', color:'#000',
                    border:'none', borderRadius:6, padding:'9px 20px', fontWeight:700, fontSize:13,
                    cursor: allDone ? 'pointer' : 'not-allowed', opacity: allDone ? 1 : 0.5 }}>
                  {saving ? 'Saving…' : anyFail ? 'Submit (Failed)' : 'Submit (Passed)'}
                </button>
                <button onClick={onClose}
                  style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                    borderRadius:6, padding:'9px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Scrap modal ───────────────────────────────────────────────────────────────

const SCRAP_REASON_CODES = [
  'Component defect', 'Assembly error', 'Solder defect', 'Firmware issue',
  'Calibration failure', 'Cosmetic damage', 'Structural damage', 'Beyond repair', 'Other',
];

function ScrapModal({ serialNumber, onClose, onSubmit, saving }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes]   = useState('');
  const inp = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'8px 12px', fontSize:13, width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--ni-char)', border:'1px solid #EC212444', borderRadius:12,
        padding:24, width:400, maxWidth:'90%' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <span style={{ fontWeight:700, fontSize:16, color:'#EC2124' }}>Scrap Device</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        <div style={{ fontSize:13, color:'var(--ni-text-muted)', marginBottom:16 }}>
          This will permanently mark{' '}
          <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--ni-text)' }}>{serialNumber}</span>
          {' '}as scrapped. This action cannot be undone from the work order.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.06em', marginBottom:5 }}>Scrap Reason *</div>
            <select value={reason} onChange={e => setReason(e.target.value)} style={inp}>
              <option value="">— Select reason —</option>
              {SCRAP_REASON_CODES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
              letterSpacing:'0.06em', marginBottom:5 }}>Notes (optional)</div>
            <input value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="Additional details…" />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => onSubmit(reason, notes)} disabled={saving || !reason}
              style={{ background:'#EC2124', color:'#fff', border:'none', borderRadius:6,
                padding:'9px 20px', fontWeight:700, fontSize:13,
                cursor: reason ? 'pointer' : 'not-allowed', opacity: reason ? 1 : 0.5 }}>
              {saving ? 'Saving…' : 'Confirm Scrap'}
            </button>
            <button onClick={onClose}
              style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
                borderRadius:6, padding:'9px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
